import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLines, Reveal, FadeIn, EASE } from "@/components/Motion";
import BrandStrip from "@/components/BrandStrip";
import { STONES, HERO_TEXTURE } from "@/data/stones";
import { useSeo } from "@/hooks/useSeo";

const quartz = STONES[0];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_TEXTURE} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 noise-overlay opacity-[0.04]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-36 pb-16 md:pb-20 w-full grid lg:grid-cols-12 gap-12 items-end">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <FadeIn delay={0.15}>
            <p data-testid="hero-overline" className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-8">
              Varun Marbles — Purveyors of fine stone
            </p>
          </FadeIn>
          <h1 data-testid="hero-headline" className="font-serif font-light tracking-tighter leading-[0.95] text-6xl sm:text-7xl lg:text-[7rem]">
            <MaskedLines
              delay={0.25}
              lines={[
                <>The art of</>,
                <><span className="italic">eternal</span> stone.</>,
              ]}
            />
          </h1>
          <FadeIn delay={0.85}>
            <p data-testid="hero-subcopy" className="mt-10 max-w-md text-base font-light leading-relaxed text-muted-foreground">
              Quartz slabs, Italian marble, granite and onyx — sourced from the world's great
              quarries, finished for spaces that are built to be remembered.
            </p>
          </FadeIn>
          <FadeIn delay={1}>
            <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-4">
              <Link
                to="/collections"
                data-testid="hero-cta-collections"
                className="group inline-flex w-full sm:w-auto justify-center items-center gap-3 bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-foreground/80"
              >
                Explore collections
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-enquire"
                className="inline-flex w-full sm:w-auto justify-center items-center gap-3 border border-foreground/25 text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-foreground hover:text-background"
              >
                Enquire
              </Link>
            </div>
          </FadeIn>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
            className="relative aspect-[4/5] overflow-hidden"
          >
            <motion.img
              src={quartz.image}
              alt="Signature quartz slab in a modern kitchen"
              data-testid="hero-image"
              style={{ y: imgY }}
              className="w-full h-[115%] object-cover"
            />
          </motion.div>
          <FadeIn delay={1.2} className="mt-4 flex justify-between items-baseline">
            <p className="text-xs tracking-[0.25em] uppercase text-secondary-foreground">Signature — Quartz Slabs</p>
            <p className="font-serif italic text-lg text-foreground/50">№ 01</p>
          </FadeIn>
        </div>
      </div>

      <FadeIn delay={1.4} className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:block">
        <ArrowDown className="w-4 h-4 text-muted-foreground animate-bounce" />
      </FadeIn>
    </section>
  );
}

function Signature() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section ref={ref} data-testid="signature-section" className="max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40">
      <div className="grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-5 relative">
          <span className="font-serif text-8xl md:text-[14rem] leading-none text-secondary-foreground/15 absolute -top-10 md:-top-24 -left-2 md:-left-4 select-none" aria-hidden>
            01
          </span>
          <Reveal className="relative">
            <div className="aspect-[4/5] overflow-hidden">
              <motion.img
                src={quartz.image}
                alt="Quartz slab detail"
                style={{ y }}
                className="w-full h-[112%] object-cover transition-transform duration-700 hover:scale-[1.03]"
              />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">Our signature</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.02]">
              Quartz, <span className="italic">perfected.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base font-light leading-relaxed text-muted-foreground max-w-lg">
              {quartz.description}
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-3">
              {quartz.finishes.map((f) => (
                <span key={f} className="text-xs tracking-[0.15em] uppercase border border-border px-4 py-2 text-secondary-foreground">
                  {f}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.26}>
            <Link
              to="/contact?stone=quartz-slabs"
              data-testid="signature-enquire-link"
              className="group mt-12 inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-foreground"
            >
              <span className="border-b border-foreground/40 pb-1 transition-colors duration-300 group-hover:border-foreground">
                Enquire about quartz
              </span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const CHAPTERS = [
  {
    n: "01",
    title: "Source",
    body: "We travel to quarries across Italy, Brazil, Turkey and India, selecting blocks at the face — before they ever become slabs.",
  },
  {
    n: "02",
    title: "Select",
    body: "Every slab is hand-inspected for vein, tone and translucency. Fewer than one in ten makes it into our gallery.",
  },
  {
    n: "03",
    title: "Serve",
    body: "From architects to first-time homeowners, we guide each stone from our yard to your space — cut, finished and installed right.",
  },
];

function Manifesto() {
  return (
    <section data-testid="manifesto-section" className="bg-secondary/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40">
        <Reveal>
          <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">The manifesto</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight max-w-xl leading-[1.05]">
            Three vows, kept <span className="italic">in stone.</span>
          </h2>
        </Reveal>
        <div className="mt-14 md:hidden">
          <div
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-6 px-6"
            style={{ scrollbarWidth: "none" }}
            data-testid="manifesto-swipe"
          >
            {CHAPTERS.map((c) => (
              <div key={c.n} className="snap-start shrink-0 w-[80%] border border-border bg-background p-6">
                <p className="font-serif text-5xl font-light text-secondary-foreground/40 mb-4">{c.n}</p>
                <h3 className="font-serif text-xl font-light tracking-tight mb-3">{c.title}</h3>
                <p className="text-sm font-light leading-relaxed text-muted-foreground">{c.body}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-secondary-foreground mt-3 text-center">
            Swipe →
          </p>
        </div>

        <div className="mt-20 hidden md:grid md:grid-cols-3 gap-10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12} className="border-t border-foreground/15 pt-8">
              <p className="font-serif text-6xl font-light text-secondary-foreground/40 mb-6">{c.n}</p>
              <h3 className="font-serif text-2xl font-light tracking-tight mb-4">{c.title}</h3>
              <p className="text-sm font-light leading-relaxed text-muted-foreground">{c.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function MosaicTile({ stone, className = "" }) {
  return (
    <Link
      to={`/collections/${stone.slug}`}
      data-testid={`preview-stone-${stone.slug}`}
      className={`group relative block overflow-hidden ${className}`}
    >
      <img
        src={stone.image}
        alt={stone.name}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
      />
      <div className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/35" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-black/10" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
        <p className="text-white/70 text-[9px] md:text-[10px] tracking-[0.3em] mb-1">{stone.number}</p>
        <p className="font-serif text-white text-sm sm:text-lg md:text-2xl font-light tracking-tight leading-tight">
          {stone.name}
        </p>
      </div>
    </Link>
  );
}

function EnquireTile({ className = "" }) {
  return (
    <Link
      to="/contact"
      data-testid="preview-tile-enquire"
      className={`group relative flex flex-col justify-center items-center md:items-start p-5 md:p-6 bg-primary text-primary-foreground ${className}`}
    >
      <p className="text-[10px] tracking-[0.3em] uppercase text-primary-foreground/50 mb-1">Your project</p>
      <p className="font-serif text-lg md:text-xl font-light italic flex items-center gap-2">
        Enquire
        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
      </p>
    </Link>
  );
}

function CollectionsPreview() {
  return (
    <section data-testid="collections-preview" className="max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40">
      <Reveal className="flex flex-wrap items-end justify-between gap-8 mb-20">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">The collections</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.02]">
            Seven families <span className="italic">of stone.</span>
          </h2>
        </div>
        <Link
          to="/collections"
          data-testid="preview-view-all-link"
          className="group inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase"
        >
          <span className="border-b border-foreground/40 pb-1 transition-colors duration-300 group-hover:border-foreground">
            View all collections
          </span>
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>

      <Reveal>
        <div
          data-testid="collections-mosaic"
          className="grid grid-cols-2 md:grid-cols-6 auto-rows-[150px] sm:auto-rows-[160px] lg:auto-rows-[190px] grid-flow-dense gap-0"
        >
          <MosaicTile stone={STONES[0]} className="col-span-2 md:col-span-3 row-span-2" />
          <MosaicTile stone={STONES[1]} className="col-span-1 md:col-span-3 row-span-1" />
          <MosaicTile stone={STONES[2]} className="col-span-1 md:col-span-2 row-span-1" />
          <MosaicTile stone={STONES[3]} className="col-span-1 row-span-1" />
          <MosaicTile stone={STONES[4]} className="col-span-1 md:col-span-2 row-span-1" />
          <MosaicTile stone={STONES[5]} className="col-span-1 md:col-span-2 row-span-1" />
          <MosaicTile stone={STONES[6]} className="col-span-1 row-span-1" />
          <EnquireTile className="col-span-2 md:col-span-1 row-span-1" />
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  useSeo({
    title: "Varun Marbles | Quartz Slabs, Italian Marble, Granite & Onyx in Gurgaon",
    description:
      "Premium quartz slabs, Italian & Indian marble, granite and onyx in Gurgaon. Authorised KalingaStone & Paradigm Quartz dealers — visit our marble market showrooms.",
    path: "/",
  });
  return (
    <>
      <Hero />
      <BrandStrip />
      <Signature />
      <Manifesto />
      <CollectionsPreview />
    </>
  );
}
